import {
  type JobProgressionProps,
  type ProgressionFlow,
  type ProgressionStepProps,
  type ProgressionStepStatus,
} from '@/features/jobs/taxes/progression/JobProgression'
import {
  JobProgressionStepStatus as StatusProto,
  type ProgressionFound as ProgressionFoundProto,
  type JobProgressionStep as StepProto,
  JobProgressionFlow as FlowProto,
  type JobProgressionRealtimeEvent as JobProgressionRealtimeEventProto,
} from '@taxfyle/api-internal/internal/job_progression_pb'
import { Id } from '@/infra/id'
import { match } from '@/utils/pattern-match-util'
import { safeTimestampToDate } from '@/utils/grpc-util'

export function mapProgressionFoundToProps(
  progression: ProgressionFoundProto | undefined,
): JobProgressionProps {
  return {
    jobId: Id.assert(progression?.getJobId()),
    flow: mapFlowProtoToProgressionFlow(progression?.getProgressionFlow()),
    recordedSteps: progression?.getStepsList().map(mapStepProtoToProps) ?? [],
  }
}

export function mapProgressionRealtimeEventToProps(
  progression: JobProgressionRealtimeEventProto | undefined,
): JobProgressionProps {
  return {
    jobId: Id.assert(progression?.getJobId()),
    flow: mapFlowProtoToProgressionFlow(progression?.getProgressionFlow()),
    recordedSteps:
      progression?.getProgressionStepsList().map(mapStepProtoToProps) ?? [],
  }
}

export function mapFlowProtoToProgressionFlow(
  proto: FlowProto | undefined,
): ProgressionFlow {
  return match(proto ?? FlowProto.NONE, {
    [FlowProto.NONE]: 'Retail', // Fall back to retail
    [FlowProto.RETAIL]: 'Retail',
    [FlowProto.OUTSOURCING]: 'Outsourcing',
    [FlowProto.ESSENTIAL]: 'Essential',
    [FlowProto.PREPARATION]: 'Preparation',
    [FlowProto.REVIEW]: 'Review',
    [FlowProto.PERSONAL_EXTENSION]: 'PersonalExtension',
    [FlowProto.BUSINESS_EXTENSION]: 'BusinessExtension',
    [FlowProto.OUTSOURCING_EXTENSION]: 'OutsourcingExtension',
  })
}

export function mapStepProtoToProps(proto: StepProto): ProgressionStepProps {
  const responseProto = proto.getResponse()
  return {
    id: proto.getId(),
    createdAt: safeTimestampToDate(proto.getDateCreated()),
    step: mapStepStatusProtoToStepStatus(proto.getProgressionStep()),
    response: responseProto
      ? {
          step: mapStepStatusProtoToStepStatus(responseProto),
          respondedAt: safeTimestampToDate(proto.getDateResponded()),
        }
      : null,
  }
}

export function mapStepStatusProtoToStepStatus(
  progressionStep: StatusProto,
): ProgressionStepStatus {
  return match(progressionStep, {
    [StatusProto.PROGRESSION_STEP_UNSPECIFIED]: 'NotStarted',
    [StatusProto.JOB_STARTED]: 'JobStarted',
    [StatusProto.OPEN_ITEMS]: 'OpenItems',
    [StatusProto.DRAFT_IN_REVIEW]: 'DraftInReview',
    [StatusProto.DRAFT_REJECTED]: 'DraftRejected',
    [StatusProto.DRAFT_APPROVED]: 'DraftApproved',
    [StatusProto.NOT_STARTED]: 'NotStarted',
    [StatusProto.AUTHORIZATION_REQUESTED]: 'AuthorizationRequested',
    [StatusProto.AUTHORIZATION_SIGNED]: 'AuthorizationSigned',
    [StatusProto.RETURN_FILED]: 'ReturnFiled',
    [StatusProto.DELIVERABLE_COMPLETED]: 'DeliverableCompleted',
    [StatusProto.PREPARATION_COMPLETED]: 'PreparationCompleted',
    [StatusProto.REVIEW_COMPLETED]: 'ReviewCompleted',
    [StatusProto.EXTENSION_IN_PROGRESS]: 'ExtensionInProgress',
    [StatusProto.EXTENSION_CONFIRMED]: 'ExtensionConfirmed',
    [StatusProto.EXTENSION_ACCEPTED]: 'ExtensionAccepted',
    [StatusProto.EXTENSION_REJECTED]: 'ExtensionRejected',
    [StatusProto.PROGRESSION_WAITING_ON_CLIENT]: 'ProgressionWaitingOnClient',
    [StatusProto.READY_TO_FILE]: 'ReadyToFile',
  })
}
